import { useState, useEffect, useRef } from 'react';
import { useApiClient } from '../../../api/client';
import { useToast } from '../../../context/ToastContext';
import { safeJson, fmtDate } from '../../../utils';
import type { ChangeRequest, StakeholderResponse } from '../../../types';
import StatusBadge from '../../../components/StatusBadge';

interface Props { record: ChangeRequest; cid: string; onRefresh: () => Promise<void>; }

export default function SignoffSection({ record, cid, onRefresh }: Props) {
  const api = useApiClient();
  const { showToast } = useToast();
  const [rows, setRows]         = useState<StakeholderResponse[]>([]);
  const [loading, setLoading]   = useState(true);
  const [resubmitting, setResubmitting] = useState(false);
  const timerRef      = useRef<ReturnType<typeof setInterval> | null>(null);
  const advancedRef   = useRef(false);
  const plan = safeJson<{ uploadedFileUrl?: string; goLiveDate?: string; planVersion?: string }>(record.implementationPlan, {});

  async function loadRows() {
    try {
      const data = await api.get<StakeholderResponse[]>(`/changes/${encodeURIComponent(cid)}/responses`);
      setRows(data);
      const allDone = data.length > 0 && data.every(r => r.signoffStatus === 'Signed Off' || r.signoffStatus === 'Not Required');
      if (allDone) {
        if (timerRef.current) clearInterval(timerRef.current);
        // Server auto-advanced to Ready for Approval — refresh parent to switch section
        if (!advancedRef.current) {
          advancedRef.current = true;
          await onRefresh();
        }
      }
    } catch (err) {
      showToast((err as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
    timerRef.current = setInterval(loadRows, 60000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [cid]);

  const signedOff = rows.filter(r => r.signoffStatus === 'Signed Off').length;
  const pending   = rows.filter(r => r.signoffStatus === 'Pending').length;
  const concerns  = rows.filter(r => r.signoffStatus === 'Concern Raised').length;
  const allDone   = rows.length > 0 && rows.every(r => r.signoffStatus === 'Signed Off' || r.signoffStatus === 'Not Required');

  async function resubmitPlan() {
    setResubmitting(true);
    try {
      await api.patch(`/changes/${encodeURIComponent(cid)}/resubmit-plan`, {});
      showToast('Plan returned for revision. Sign-off statuses reset.', 'success');
      await onRefresh();
    } catch (err) {
      showToast((err as Error).message, 'error');
    } finally {
      setResubmitting(false);
    }
  }


  return (
    <div className="section-panel">
      <div className="section-panel__header">
        <h2 className="section-panel__title"><i className="fa-solid fa-signature" aria-hidden="true" /> Step 4 — Plan Sign-Off Dashboard</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <span className="badge badge--accent">Signed Off: {signedOff}</span>
          <span className="badge badge--warn">Pending: {pending}</span>
          <span className="badge badge--danger">Concern Raised: {concerns}</span>
        </div>
      </div>
      <div className="section-panel__body">
        <div className="field-display-grid" style={{ marginBottom: 16 }}>
          <div className="field-display"><div className="field-display__label">Change ID</div><div className="field-display__value change-id-mono">{record.changeId}</div></div>
          <div className="field-display"><div className="field-display__label">Go-Live Date</div><div className="field-display__value">{fmtDate(plan.goLiveDate)}</div></div>
          <div className="field-display"><div className="field-display__label">Plan Version</div><div className="field-display__value">{plan.planVersion ?? '—'}</div></div>
          <div className="field-display"><div className="field-display__label">Sign-Off Deadline</div><div className="field-display__value">{fmtDate(record.signoffDeadline)}</div></div>
        </div>
        {plan.uploadedFileUrl && (
          <div style={{ marginBottom: 20 }}>
            <a href={plan.uploadedFileUrl} target="_blank" rel="noopener noreferrer" className="btn btn--secondary btn--sm">
              <i className="fa-solid fa-file-lines" aria-hidden="true" /> View Plan Document
            </a>
          </div>
        )}

        <div className="table-wrapper">
          <table className="table" aria-label="Plan sign-offs">
            <thead>
              <tr><th>Name</th><th>Department</th><th>Sign-Off Status</th><th>Sign-Off Date</th><th>Concern Detail</th></tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5}><div className="loading-state" style={{ padding: 32 }}><div className="spinner" /></div></td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--ink-dim)', padding: '24px 0' }}>No stakeholders assigned to this change.</td></tr>
              )}
              {rows.map(r => (
                <tr key={r.id}>
                  <td data-label="Name">{r.teamLeadName}</td>
                  <td data-label="Department">{r.department}</td>
                  <td data-label="Sign-Off Status"><StatusBadge status={r.signoffStatus} variant="response" /></td>
                  <td data-label="Sign-Off Date">{fmtDate(r.signoffDate)}</td>
                  <td data-label="Concern Detail" style={{ fontSize: '0.8125rem' }}>{r.concernDetail ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {concerns > 0 && (
          <div style={{ marginTop: 20, padding: '14px 16px', background: 'var(--warn-light, #fffbeb)', border: '1px solid var(--warn)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ color: 'var(--warn)' }} aria-hidden="true" />
            <span style={{ flex: 1, fontSize: '0.875rem' }}>
              {concerns === 1 ? '1 stakeholder has raised a concern.' : `${concerns} stakeholders have raised concerns.`} Review the concern detail above, then revise and resubmit the plan.
            </span>
            <button className={`btn btn--warn${resubmitting ? ' is-loading' : ''}`} disabled={resubmitting} onClick={resubmitPlan}>
              <i className="fa-solid fa-rotate-left" aria-hidden="true" /> Revise &amp; Resubmit Plan
            </button>
          </div>
        )}

        {allDone && (
          <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 10, color: 'var(--ink-dim)', fontSize: '0.875rem' }}>
            <div className="spinner spinner--sm" />
            All sign-offs received — advancing to approval…
          </div>
        )}
      </div>
    </div>
  );
}
